import React, {
  startTransition, useCallback, useEffect, useMemo, useState,
} from 'react'

import { Context } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, string } from 'prop-types'

import { FILTER_NAME, EVENT_SOURCE } from 'Pods/SearchPage/Shared/Tracking/constants'
import { FilterTrackingProvider } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import { trackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/searchPageTracker'
import { fetchResults } from 'Pods/SearchPage/Store/actions'
import { setFileTypes } from 'Pods/SearchPage/Store/actions/fileTypes'
import { refreshTrackingInfo } from 'Pods/SearchPage/Store/actions/trackingInfo'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import Pagination from 'Pods/Shared/Components/Pagination/Pagination'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import I18n from 'Pods/Shared/I18n'
import AdultContentModal from 'Pods/Shared/LazyAdultContentModal'
import ModelCard from 'Pods/Shared/ModelCard/ModelCard'
import getModelsPerRow from 'Pods/Shared/ModelCard/utils/getModelsPerRow'

import isIOS from 'Utils/isIOS'
import { EMPTY_OBJECT, NOOP } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import CrawlablePagination from './Components/CrawlablePagination/CrawlablePagination'
import EmptyState from './Components/EmptyState/EmptyState'
import PromoSection from './Components/PromoSection/PromoSection'
import useCartWishlist from './useCartWishlist'
import useResultsGallery from './useResultsGallery'
import useResultsTracking from './useResultsTracking'

const CARD_SIZE_SETTING = 'medium'
// Subset of the listing controller's AVAILABLE_PER_PAGE ([8, 10, 40, 80, 120]) that suits a grid.
const PER_PAGE_OPTIONS = [40, 80, 120]
const DEFAULT_PER_PAGE = 120
// The widest density of this grid is 6 columns (see _card-3d-model.scss), so the first 6 cards
// cover a full above-the-fold row at every breakpoint. Lazy-loading them delays LCP.
const EAGER_IMAGE_COUNT = 6
const IDLE_REFRESH_DEADLINE = 300
// Mirrors ListingPage's List.jsx mobile truncation: SSR/hydrate only the first viewport-worth of
// cards on a mobile device (the payload can carry 120), then expand to the full set (see below).
const MOBILE_TRUNCATED_COUNT = 3
const MOBILE_EXPAND_DEADLINE = 2000

const getKeywords = () => {
  if (typeof window === 'undefined') {
    return ''
  }
  return new URLSearchParams(window.location.search).get('keywords') || ''
}

function Results({
  locale = 'en',
  isMobileSSR = false,
  shouldShowAllItemsOnMobile = false,
  printModelsListing = false,
  paginationBaseUrl = '',
  keywords = '',
}) {
  // The i18next singleton stays configured with lng 'en' (non-EN bundles are only *registered*,
  // client-side via loadLocaleBundle and at SSR via registerAllLocales), so every call must name
  // the page locale explicitly or non-English pages render English (ListingPage precedent).
  const translate = (key, options) => I18n.t(key, { ns: 'rails', lng: locale, ...options })

  const results = useSearchPageStore(state => state.results)
  const meta = useSearchPageStore(state => state.meta)
  const isLoading = useSearchPageStore(state => state.isLoading)
  const fetchError = useSearchPageStore(state => state.fetchError)
  const fileTypes = useSearchPageStore(state => state.fileTypes)
  const canonicalUrl = useSearchPageStore(state => state.canonicalUrl)
  const itemListId = useSearchPageStore(state => state.itemListId)
  const gaList = useSearchPageStore(state => state.gaList)
  const trackingInfoIds = useSearchPageStore(state => state.trackingInfoIds)
  const gaAttributesById = useSearchPageStore(state => state.gaAttributesById)

  const {
    cart,
    cartItemIds,
    setCartItemIds,
    wishListItems,
    setWishListItems,
    currentUserId,
    viewerIsAdult,
    setViewerIsAdult,
    showCgtVerification,
  } = useCartWishlist()

  // Subscription label gates. All three are user-scoped, so they arrive client-side via pageConfig
  // rather than the SSR props: SearchPagesController's HTML is action-cached and its key segments
  // only on a `user_logged_in` boolean, so a gate resolved at SSR would leak the first logged-in
  // viewer's value (e.g. an admin's badges) to every other logged-in viewer of that cache entry.
  // Gated on hasMounted: usePageConfig seeds synchronously from window.pageConfig, which can
  // already exist when hydration runs - SSR rendered the gates as false, so applying them on the
  // first client render would mismatch and rebuild the whole results markup. The first render must
  // always match SSR; the gates then refine in place (fast-page pattern).
  const pageConfig = usePageConfig()
  const [hasMounted, setHasMounted] = useState(false)
  const subscriptionFlowEnabled = hasMounted && !!pageConfig?.subscriptionFlowEnabled
  const premiumListingLabelEnabled = hasMounted && !!pageConfig?.premiumExposed
  const subscriptionBadgesEnabled = hasMounted && !!pageConfig?.listingSubscriptionBadgesEnabled

  // Age-restricted cards route every interaction (image/title click, preview, add to cart)
  // through showAdultModal, so the modal must be wired for those cards to be usable at all.
  // Mirrors ListingPage's List.jsx / FreeItemsSection wiring.
  const [isAdultModalVisible, toggleAdultModal] = useState(false)
  const showAdultModal = useCallback(() => toggleAdultModal(true), [])
  const hideAdultModal = useCallback(() => toggleAdultModal(false), [])

  // Format badges toggle the store's file-type filter (ListingPage contract:
  // { name, value: string[] of file-type ids }); the store subscription then refetches and the
  // FileFormat dropdown reflects the same selection.
  const handleSelectedFiltersChange = useCallback(({ name, value }) => {
    if (name === 'fileTypes') {
      setFileTypes(value)
    }
  }, [])

  // Format badges report the label they select; ModelCard names the source.
  const handleTrackFilterChange = useCallback(({ name, value, eventSource = EVENT_SOURCE.MODEL_CARD }) => {
    if (name === 'fileTypes') {
      trackFilterSelect({ name: FILTER_NAME.FILE_FORMAT, value, eventSource })
    }
  }, [])

  // Seeded with the keywords the server echoed so the post-mount URL read is normally a no-op: a
  // change here reaches every card through the context and re-rendered the whole grid after
  // hydration. The URL still wins (window is absent during the fast-page SSR render).
  const [urlKeywords, setUrlKeywords] = useState(keywords)
  // iOS cards swap desktop hover quick-actions for a tap overlay (List.jsx precedent); detected
  // post-mount because navigator is absent at SSR and the default (false) matches server markup.
  const [isIOSDevice, setIsIOSDevice] = useState(false)

  useEffect(() => {
    setUrlKeywords(getKeywords())
    setIsIOSDevice(isIOS())
    setHasMounted(true)
  }, [])

  const {
    currentPage = 1, totalPages = 1, totalCount = 0, perPage = DEFAULT_PER_PAGE,
  } = meta || {}
  // A failed fetch leaves the previous page's results in the store (react-query keeps them cached
  // and popstate may still need them), so the grid must be suppressed explicitly - otherwise stale
  // cards read as the result of the filter the user just applied.
  const hasResults = !fetchError && results.length > 0

  // isMobileSSR comes from the mobile variant of the action cache (its key segments on
  // is_mobile), so server markup and client hydration agree on the truncated set - mobile
  // startup builds 3 card trees instead of up to 120. ModelCard fires trackLastItemInView when
  // the last truncated card nears the viewport; expanding once is permanent, like List.jsx.
  const [isListTruncated, setIsListTruncated] = useState(isMobileSSR && !shouldShowAllItemsOnMobile)
  const displayedResults = isListTruncated ? results.slice(0, MOBILE_TRUNCATED_COUNT) : results

  const trackLastItemInView = useCallback(() => {
    setIsListTruncated(false)
  }, [])

  // Expanding only when the last truncated card scrolled near grew the grid while pagination and
  // the footer were on screen and shoved them down (~0.29 CLS on mobile). Expanding at the first
  // idle moment after hydration grows it below the fold instead; the transition keeps that render
  // interruptible, and trackLastItemInView still covers a viewer who scrolls before then.
  useEffect(() => {
    if (!isListTruncated) {
      return undefined
    }
    const expand = () => startTransition(() => setIsListTruncated(false))
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(expand, { timeout: MOBILE_EXPAND_DEADLINE })
      return () => window.cancelIdleCallback(idleId)
    }
    const timeoutId = setTimeout(expand, MOBILE_EXPAND_DEADLINE)
    return () => clearTimeout(timeoutId)
  }, [isListTruncated])

  // The action/preload caches can serve results whose moderation flags changed after the cache
  // was written; re-check the uncached adult flags for every distinct result set (initial SSR
  // seed included) so newly restricted thumbnails blur without waiting for cache expiry. The same
  // request brings the GA item attributes the ecommerce events need.
  const resultIdsSignature = useMemo(() => results.map((model) => model.id).join(','), [results])

  useEffect(() => {
    if (!resultIdsSignature) {
      return undefined
    }
    // Idle-scheduled so the uncached request does not compete with hydration/LCP work right at
    // load. The timeout is deliberately short: this is a moderation check, and until it resolves a
    // newly restricted thumbnail stays unblurred, so the deadline caps that exposure rather than
    // waiting for a genuine idle window.
    // refreshTrackingInfo re-reads the store at call time, so a deferred run never patches stale ids.
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(() => refreshTrackingInfo(), { timeout: IDLE_REFRESH_DEADLINE })
      return () => window.cancelIdleCallback(idleId)
    }
    const timeoutId = setTimeout(refreshTrackingInfo, IDLE_REFRESH_DEADLINE)
    return () => clearTimeout(timeoutId)
  }, [resultIdsSignature])

  const { showGallery, handlePreviewClick, handleCloseGallery } = useResultsGallery(resultIdsSignature)

  // ModelCard inserts the open gallery as a full-width row item after the last card on the
  // selected model's line, so this must be the real per-row count for the current viewport, not a
  // constant - the shared .card-3d-model flex-basis (@include columns) is what these values map.
  // Undefined at SSR (window is absent) resolves to 1; nothing renders off it until a gallery is
  // open, so the server and first client markup still agree.
  const currentBreakpoint = useBreakpoint()
  const modelsPerRow = getModelsPerRow(currentBreakpoint, CARD_SIZE_SETTING)

  // Positions continue across pages (the legacy List.jsx offset), for GA and the cart payload.
  const pageOffset = (Math.max(Number(currentPage) || 1, 1) - 1) * (Number(perPage) || DEFAULT_PER_PAGE)

  const { isTrackingInfoAvailable, areTrackingUtilsAvailable } = useResultsTracking({
    results: displayedResults,
    resultIdsSignature,
    trackingInfoIds,
    gaAttributesById,
    itemListId,
    pageOffset,
    perPage,
    modelsPerRow,
    pageConfig: hasMounted ? pageConfig : undefined,
  })

  // ModelCard's subtree (ModelCardActionButtons + ModelCardImage) reads these keys via
  // useContext(Context). This is the minimal set - the full ListingPage context is not needed.
  const contextValue = useMemo(() => ({
    selectedFilters: { keywords: urlKeywords },
    downloadAccess: EMPTY_OBJECT,
    openDownloadModal: NOOP,
    locale,
    isLoading,
    isTrackingInfoAvailable,
    areTrackingUtilsAvailable,
  }), [isLoading, urlKeywords, locale, isTrackingInfoAvailable, areTrackingUtilsAvailable])

  // Mirrors the ListingPage default (all sections visible); cgtStandard tracks the fast-page flag.
  const cardVisibilitySettings = useMemo(() => ({
    title: true,
    type: true,
    price: true,
    format: true,
    quickActionsOnHover: true,
    preview: true,
    cgtStandard: showCgtVerification,
    suggestions: true,
  }), [showCgtVerification])

  // Localizes the shared Pagination (its own defaults are English-only). Locale is fixed per page
  // load, so the labels never recompute in practice.
  const paginationLabels = useMemo(() => ({
    showing: (start, end, total) => translate('search_page.results.showing', { start, end, total }),
    perPage: translate('search_page.results.per_page'),
    firstPage: translate('search_page.results.first_page'),
    previousPage: translate('search_page.results.previous_page'),
    nextPage: translate('search_page.results.next_page'),
    lastPage: translate('search_page.results.last_page'),
    pageInput: translate('search_page.results.page_input'),
  }), [locale])

  const handlePageChange = (page) => {
    trackFilterSelect({ name: FILTER_NAME.PAGE, value: page, eventSource: EVENT_SOURCE.PAGINATION })
    fetchResults({ page })
    // /search reloads the whole document on paging and lands at the top. Client-side paging must
    // match, or the grid is swapped out below the fold and the viewport stays mid-list. Scroll
    // before the results land so the swap happens off-screen; 'auto' mirrors a document load.
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  const handlePerPageChange = (newPerPage) => {
    trackFilterSelect({ name: FILTER_NAME.PER_PAGE, value: newPerPage, eventSource: EVENT_SOURCE.PAGINATION })
    fetchResults({ perPage: newPerPage })
  }

  return (
    <section
      className="search-page__results"
      aria-busy={isLoading}
    >
      {hasResults ? (
        <Context.Provider value={contextValue}>
          <div className="search-page__results-grid">
            {displayedResults.map((model, index) => (
              <ModelCard
                key={model.id}
                {...model}
                cart={cart}
                modelIndex={index}
                listIndex={pageOffset + index}
                lastModelIndex={displayedResults.length - 1}
                modelsPerRow={modelsPerRow}
                eagerImageCount={EAGER_IMAGE_COUNT}
                cardSizeSetting={CARD_SIZE_SETTING}
                cardVisibilitySettings={cardVisibilitySettings}
                showCgtVerification={showCgtVerification}
                viewerIsAdult={viewerIsAdult}
                currentUserId={currentUserId}
                cartItemIds={cartItemIds}
                setCartItemIds={setCartItemIds}
                wishListItems={wishListItems}
                setWishListItems={setWishListItems}
                currentSelectedFileTypes={fileTypes}
                setSelectedFilters={handleSelectedFiltersChange}
                subscriptionBadgesEnabled={subscriptionBadgesEnabled}
                subscriptionFlowEnabled={subscriptionFlowEnabled}
                premiumListingLabelEnabled={premiumListingLabelEnabled}
                isIOSDevice={isIOSDevice}
                gaList={gaList}
                itemListId={itemListId}
                trackFilterChange={handleTrackFilterChange}
                trackLastItemInView={trackLastItemInView}
                showAdultModal={showAdultModal}
                showGallery={showGallery}
                onPreviewClick={handlePreviewClick}
                onCloseGallery={handleCloseGallery}
              />
            ))}
          </div>
        </Context.Provider>
      ) : (
        !isLoading && (fetchError ? (
          <p className="search-page__results-empty" role="alert">
            {translate('search_page.results.error')}
          </p>
        ) : (
          <FilterTrackingProvider eventSource={EVENT_SOURCE.EMPTY_STATE}>
            <EmptyState />
          </FilterTrackingProvider>
        ))
      )}

      {/* `alwaysShow` keeps the per-page selector for a single page of results, but only when
          there ARE results: NumberOfItems would otherwise render "Showing 1-0 of 0" on an empty
          or errored fetch, with page controls under the error message. */}
      {hasResults && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.max(totalPages, 1)}
          totalCount={totalCount}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          perPageOptions={PER_PAGE_OPTIONS}
          labels={paginationLabels}
          ariaLabel={translate('search_page.results.pagination')}
          alwaysShow
          // Print listings swap the button controls for the legacy anchor pagination: crawlers only
          // follow real page URLs (ListingPage's PaginationLinks precedent). canonicalUrl is empty
          // until the first fetch, so the SSR anchors use the localized request URL.
          pageControls={printModelsListing ? (
            <CrawlablePagination
              baseUrl={canonicalUrl || paginationBaseUrl}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              labels={paginationLabels}
            />
          ) : null}
        />
      )}

      {hasResults && <PromoSection />}

      {isAdultModalVisible && (
        <AdultContentModal setViewerIsAdult={setViewerIsAdult} hideAdultModal={hideAdultModal} />
      )}

      {/* Concise SR-only live status. A polite region around the whole grid would make assistive
          tech re-announce up to 120 card titles/prices on every result replacement (and errors
          twice through the nested role="alert"); announce only the settled result count instead.
          Always mounted: live regions only announce changes to content that existed at load. */}
      <p className="search-page__results-status" role="status">
        {!isLoading && !fetchError
          ? translate('search_page.results.results_count', { total: totalCount })
          : ''}
      </p>
    </section>
  )
}

Results.propTypes = {
  locale: string,
  isMobileSSR: bool,
  shouldShowAllItemsOnMobile: bool,
  printModelsListing: bool,
  paginationBaseUrl: string,
  keywords: string,
}

export default Results
